const CLIENT_ROLE_ROUTE_KEY = 'clientRoleRoute';
const CLIENT_ROLE_NAME_KEY = 'clientRoleName';

/** Maps API role_name (e.g. Super-Admin) to URL prefix (e.g. super-admin). */
export function roleNameToRoutePrefix(
  roleName: string | null | undefined
): string {
  if (!roleName?.trim()) {
    return '';
  }
  return roleName.trim().toLowerCase().replace(/\s+/g, '-');
}

export function persistClientRoleRoute(
  roleName: string | null | undefined
): void {
  const prefix = roleNameToRoutePrefix(roleName);
  if (prefix) {
    sessionStorage.setItem(CLIENT_ROLE_ROUTE_KEY, prefix);
  }
  if (roleName?.trim()) {
    sessionStorage.setItem(CLIENT_ROLE_NAME_KEY, roleName.trim());
  }
}

export function getStoredClientRoleName(): string | null {
  return sessionStorage.getItem(CLIENT_ROLE_NAME_KEY);
}

/**
 * Route prefix for navigation (admin, super-admin, client, …).
 * Uses in-memory clientRole, then sessionStorage, then current URL.
 */
export function getClientRoleRoutePrefix(
  clientRole?: string | null,
  currentPath?: string
): string {
  const fromRole = roleNameToRoutePrefix(clientRole);
  if (fromRole) {
    return fromRole;
  }

  const stored = sessionStorage.getItem(CLIENT_ROLE_ROUTE_KEY);
  if (stored) {
    return stored;
  }

  const path = currentPath ?? '';
  const segment = path.split('?')[0].split('#')[0].split('/').filter(Boolean)[0];
  if (
    segment &&
    segment !== 'forbidden' &&
    segment !== 'fourzerofour'
  ) {
    return segment;
  }

  return 'admin';
}

const URL_SEGMENT_TO_PANEL_ROLE: Record<string, string> = {
  coordinator: 'Coordinator',
  evaluator: 'Evaluator',
  client: 'Client',
};

/** Display label for toolbar role badge from the first URL segment. */
export function getPanelRoleLabelFromUrl(
  urlOrPath?: string
): string | null {
  const path =
    urlOrPath ??
    (typeof window !== 'undefined' ? window.location.pathname : '');
  const segment = path.split('?')[0].split('#')[0].split('/').filter(Boolean)[0];
  return URL_SEGMENT_TO_PANEL_ROLE[segment] ?? null;
}

/**
 * Coordinator module: prefer Coordinator/Evaluator from URL (/coordinator vs /evaluator).
 */
export function getCoordinatorModuleDisplayRole(
  url: string,
  fallbackRole?: string | null
): string {
  const fromUrl = getPanelRoleLabelFromUrl(url);
  if (fromUrl === 'Coordinator' || fromUrl === 'Evaluator') {
    return fromUrl;
  }
  return fallbackRole?.trim() || '';
}

/** URL path prefix for coordinator module navigation (`/coordinator` or `/evaluator`). */
export function getCoordinatorPanelRoutePrefix(
  url: string
): 'coordinator' | 'evaluator' | null {
  const segment = url
    .split('?')[0]
    .split('#')[0]
    .split('/')
    .filter(Boolean)[0];
  if (segment === 'coordinator' || segment === 'evaluator') {
    return segment;
  }
  return null;
}

export function isPrivilegedLoginRole(
  loginRole: string | null | undefined
): boolean {
  const role = loginRole?.trim();
  return role === 'Admin' || role === 'Super-Admin';
}

export type CoordinatorPanelRole = 'Coordinator' | 'Evaluator';

const PRIVILEGED_PANEL_OVERRIDE_ROLES = new Set(['Admin', 'Super-Admin']);

/** True when login role may not access the coordinator or evaluator panel in the URL. */
export function isCoordinatorPanelAccessDenied(
  loginRole: string | null | undefined,
  requiredPanelRole: CoordinatorPanelRole
): boolean {
  const role = loginRole?.trim();
  if (!role) {
    return false;
  }
  if (PRIVILEGED_PANEL_OVERRIDE_ROLES.has(role)) {
    return false;
  }
  if (role === 'Coordinator' || role === 'Evaluator') {
    return role !== requiredPanelRole;
  }
  return true;
}
